import React, { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
    Download,
    Eye,
    LogOut,
    RefreshCw,
    Search,
    Trash2,
    Users,
    X,
} from "lucide-react";
import type { SheetData } from "write-excel-file/browser";
import { Link } from "react-router-dom";

type Registration = {
    id: number;
    firstName: string;
    lastName: string;
    gender: string;
    email: string;
    phone: string;
    member: string;
    invitedBy: string;
    affiliate: string;
    attended: string;
    localChurch: string;
    registrationYear: number;
    createdAt: string | null;
};

type ApiResponse = {
    success: boolean;
    message?: string;
    token?: string;
    users?: Registration[];
    years?: number[];
};

const API_URL =
    process.env.REACT_APP_ADMIN_API_URL ||
    "https://thenewbrook.com/api/haystack/admin-api.php";
const TOKEN_KEY = "haystack_admin_token";

const Admin = () => {
    const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
    const [password, setPassword] = useState("");
    const [users, setUsers] = useState<Registration[]>([]);
    const [query, setQuery] = useState("");
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [memberFilter, setMemberFilter] = useState("all");
    const [availableYears, setAvailableYears] = useState<number[]>([]);
    const [selectedUser, setSelectedUser] = useState<Registration | null>(null);
    const [isLoading, setIsLoading] = useState(Boolean(token));
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [error, setError] = useState("");

    const clearSession = useCallback(() => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setUsers([]);
        setSelectedUser(null);
    }, []);

    const request = useCallback(
        async (
            action: string,
            options: RequestInit = {},
            authToken = token,
            params: Record<string, string> = {}
        ) => {
            const headers = new Headers(options.headers);
            headers.set("Content-Type", "application/json");
            if (authToken) headers.set("Authorization", `Bearer ${authToken}`);

            const url = new URL(API_URL);
            url.searchParams.set("action", action);
            Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));

            const response = await fetch(url.toString(), {
                ...options,
                headers,
            });
            const data: ApiResponse = await response.json().catch(() => ({
                success: false,
                message: "The server returned an invalid response.",
            }));

            if (response.status === 401 && action !== "login") clearSession();
            if (!response.ok || !data.success) {
                throw new Error(data.message || "The request could not be completed.");
            }
            return data;
        },
        [clearSession, token]
    );

    const loadUsers = useCallback(async () => {
        if (!token) return;
        setIsLoading(true);
        setError("");
        try {
            const data = await request("list", {}, token, {
                year: String(selectedYear),
                member: memberFilter,
            });
            setUsers(data.users || []);
            setAvailableYears(data.years || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not load registrations.");
        } finally {
            setIsLoading(false);
        }
    }, [memberFilter, request, selectedYear, token]);

    useEffect(() => {
        window.scrollTo(0, 0);
        loadUsers();
    }, [loadUsers]);

    const handleLogin = async (event: FormEvent) => {
        event.preventDefault();
        if (!password) return;
        setIsLoggingIn(true);
        setError("");
        try {
            const data = await request(
                "login",
                { method: "POST", body: JSON.stringify({ password }) },
                ""
            );
            const newToken = data.token || "";
            sessionStorage.setItem(TOKEN_KEY, newToken);
            setToken(newToken);
            setPassword("");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Login failed.");
        } finally {
            setIsLoggingIn(false);
        }
    };

    const handleLogout = async () => {
        try {
            await request("logout", { method: "POST", body: "{}" });
        } catch {
            // The local session should still be cleared if the server is unavailable.
        } finally {
            clearSession();
        }
    };

    const handleDelete = async (user: Registration) => {
        const confirmed = window.confirm(
            `Delete the registration for ${user.firstName} ${user.lastName}? This cannot be undone.`
        );
        if (!confirmed) return;

        setDeletingId(user.id);
        setError("");
        try {
            await request("delete", {
                method: "DELETE",
                body: JSON.stringify({ id: user.id }),
            });
            setUsers((current) => current.filter((item) => item.id !== user.id));
            if (selectedUser?.id === user.id) setSelectedUser(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not delete registration.");
        } finally {
            setDeletingId(null);
        }
    };

    const filteredUsers = useMemo(() => {
        const search = query.trim().toLowerCase();
        if (!search) return users;
        return users.filter((user) =>
            [
                user.firstName,
                user.lastName,
                user.email,
                user.phone,
                user.localChurch,
                user.affiliate,
                String(user.registrationYear),
            ].some((value) => value?.toLowerCase().includes(search))
        );
    }, [query, users]);

    const downloadExcel = async () => {
        setIsExporting(true);
        setError("");
        try {
            const { default: writeXlsxFile } = await import("write-excel-file/browser");
            const headings = [
                "ID",
                "First name",
                "Last name",
                "Gender",
                "Email",
                "Phone",
                "Attended previous edition",
                "NewBrook member",
                "Invited by / heard through",
                "Affiliate",
                "Local church",
                "Registration year",
                "Created at",
            ];
            const sheetData: SheetData = [
                headings.map((value) => ({
                    value,
                    fontWeight: "bold",
                    textColor: "#ffffff",
                    backgroundColor: "#613a00",
                    height: 24,
                })),
                ...filteredUsers.map((user) => [
                    user.id,
                    user.firstName,
                    user.lastName,
                    user.gender,
                    user.email,
                    { value: String(user.phone), type: String },
                    user.attended,
                    user.member,
                    user.invitedBy,
                    user.affiliate,
                    user.localChurch,
                    user.registrationYear,
                    user.createdAt || "",
                ]),
            ];
            await writeXlsxFile(sheetData, {
                sheet: "Registrations",
                stickyRowsCount: 1,
                columns: [
                    { width: 10 },
                    { width: 20 },
                    { width: 20 },
                    { width: 12 },
                    { width: 32 },
                    { width: 20 },
                    { width: 25 },
                    { width: 20 },
                    { width: 30 },
                    { width: 30 },
                    { width: 30 },
                    { width: 18 },
                    { width: 22 },
                ],
            }).toFile(
                `haystack-registrations-${new Date().toISOString().slice(0, 10)}.xlsx`
            );
        } catch {
            setError("The Excel file could not be generated. Please try again.");
        } finally {
            setIsExporting(false);
        }
    };

    if (!token) {
        return (
            <main className="flex min-h-screen items-center justify-center bg-[#f5f2ec] px-6 py-12">
                <form
                    onSubmit={handleLogin}
                    className="w-full max-w-md rounded-2xl border border-black/10 bg-white p-7 shadow-xl sm:p-10"
                >
                    <p className="font-clashSemibold text-sm uppercase tracking-[0.25em] text-[#613A00]">
                        Haystack Conference
                    </p>
                    <h1 className="mt-3 font-clashSemibold text-3xl text-[#131313]">
                        Admin sign in
                    </h1>
                    <p className="mt-3 font-inter text-sm leading-6 text-black/60">
                        Enter the admin password to manage conference registrations.
                    </p>
                    <label className="mt-8 block font-inter text-sm font-semibold text-[#131313]">
                        Admin password
                        <input
                            type="password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            autoComplete="current-password"
                            required
                            className="mt-2 w-full rounded-lg border border-black/20 px-4 py-3 font-normal outline-none transition focus:border-[#613A00] focus:ring-2 focus:ring-[#613A00]/15"
                        />
                    </label>
                    {error && (
                        <p role="alert" className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                            {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={isLoggingIn}
                        className="mt-6 w-full rounded-lg bg-[#613A00] px-5 py-3 font-clashMedium text-white transition hover:bg-[#4b2d00] disabled:cursor-wait disabled:opacity-60"
                    >
                        {isLoggingIn ? "Signing in…" : "Sign in"}
                    </button>
                </form>
            </main>
        );
    }

    return (
        <main className="min-h-screen bg-[#f5f2ec] px-4 py-6 text-[#131313] sm:px-8 lg:px-12 lg:py-10">
            <div className="mx-auto max-w-[1500px]">
                <header className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="font-clashSemibold text-sm uppercase tracking-[0.25em] text-[#613A00]">
                            Haystack Conference
                        </p>
                        <h1 className="mt-2 font-clashSemibold text-3xl sm:text-4xl">
                            {selectedYear} Registrations
                        </h1>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Link
                            to="/admin/analysis"
                            className="inline-flex items-center rounded-lg bg-[#613A00] px-4 py-2.5 font-inter text-sm font-semibold text-white hover:bg-[#4b2d00]"
                        >
                            View analysis
                        </Link>
                        <button
                            onClick={handleLogout}
                            className="inline-flex w-fit items-center gap-2 rounded-lg border border-black/15 bg-white px-4 py-2.5 font-inter text-sm font-semibold hover:bg-black/5"
                        >
                            <LogOut size={17} /> Sign out
                        </button>
                    </div>
                </header>

                <section className="mt-8 grid gap-4 sm:grid-cols-2">
                    <div className="rounded-xl border border-black/10 bg-white p-5 shadow-sm">
                        <div className="flex items-center gap-3 text-black/55">
                            <Users size={20} />
                        <span className="font-inter text-sm">{selectedYear} registrations</span>
                        </div>
                        <p className="mt-3 font-clashSemibold text-3xl">{users.length}</p>
                    </div>
                    <div className="rounded-xl border border-black/10 bg-white p-5 shadow-sm">
                        <p className="font-inter text-sm text-black/55">NewBrook members</p>
                        <p className="mt-3 font-clashSemibold text-3xl">
                            {users.filter((user) => user.member === "yes").length}
                        </p>
                    </div>
                </section>

                <section className="mt-5 overflow-hidden rounded-xl border border-black/10 bg-white shadow-sm">
                    <div className="flex flex-col gap-3 border-b border-black/10 p-4 xl:flex-row xl:items-center xl:justify-between">
                        <label className="flex w-full items-center gap-2 rounded-lg border border-black/15 px-3 py-2.5 md:max-w-md">
                            <Search size={18} className="text-black/45" />
                            <span className="sr-only">Search registrations</span>
                            <input
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder="Search name, email, phone or church"
                                className="w-full bg-transparent font-inter text-sm outline-none"
                            />
                        </label>
                        <div className="flex flex-wrap gap-2">
                            <label className="sr-only" htmlFor="registration-year">Registration year</label>
                            <select
                                id="registration-year"
                                value={selectedYear}
                                onChange={(event) => setSelectedYear(Number(event.target.value))}
                                className="rounded-lg border border-black/15 bg-white px-3 py-2.5 font-inter text-sm outline-none focus:border-[#613A00]"
                            >
                                {Array.from(new Set([new Date().getFullYear(), ...availableYears]))
                                    .sort((a, b) => b - a)
                                    .map((year) => <option key={year} value={year}>{year}</option>)}
                            </select>
                            <label className="sr-only" htmlFor="member-filter">Membership</label>
                            <select
                                id="member-filter"
                                value={memberFilter}
                                onChange={(event) => setMemberFilter(event.target.value)}
                                className="rounded-lg border border-black/15 bg-white px-3 py-2.5 font-inter text-sm outline-none focus:border-[#613A00]"
                            >
                                <option value="all">All registrations</option>
                                <option value="yes">Members</option>
                                <option value="no">Non-members</option>
                            </select>
                            <button
                                onClick={loadUsers}
                                disabled={isLoading}
                                className="inline-flex items-center gap-2 rounded-lg border border-black/15 px-4 py-2.5 font-inter text-sm font-semibold hover:bg-black/5 disabled:opacity-50"
                            >
                                <RefreshCw size={17} className={isLoading ? "animate-spin" : ""} />
                                Refresh
                            </button>
                            <button
                                onClick={downloadExcel}
                                disabled={!filteredUsers.length || isExporting}
                                className="inline-flex items-center gap-2 rounded-lg bg-[#613A00] px-4 py-2.5 font-inter text-sm font-semibold text-white hover:bg-[#4b2d00] disabled:opacity-50"
                            >
                                <Download size={17} /> {isExporting ? "Preparing…" : "Download Excel"}
                            </button>
                        </div>
                    </div>

                    {error && (
                        <p role="alert" className="m-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                            {error}
                        </p>
                    )}

                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[1100px] border-collapse text-left font-inter text-sm">
                            <thead className="bg-[#f7f5f1] text-xs uppercase tracking-wider text-black/55">
                                <tr>
                                    <th className="px-5 py-4 font-semibold">Name</th>
                                    <th className="px-5 py-4 font-semibold">Email</th>
                                    <th className="px-5 py-4 font-semibold">Phone</th>
                                    <th className="px-5 py-4 font-semibold">Gender</th>
                                    <th className="px-5 py-4 font-semibold">Attended</th>
                                    <th className="px-5 py-4 font-semibold">Member</th>
                                    <th className="px-5 py-4 font-semibold">Registered</th>
                                    <th className="px-5 py-4 text-right font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-black/10">
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={8} className="px-5 py-14 text-center text-black/50">
                                            Loading registrations…
                                        </td>
                                    </tr>
                                ) : filteredUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="px-5 py-14 text-center text-black/50">
                                            {query ? "No registrations match your search." : "No registrations yet."}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredUsers.map((user) => (
                                        <tr key={user.id} className="hover:bg-[#faf9f7]">
                                            <td className="px-5 py-4 font-semibold">
                                                {user.firstName} {user.lastName}
                                            </td>
                                            <td className="px-5 py-4 text-black/70">{user.email}</td>
                                            <td className="px-5 py-4 text-black/70">{user.phone}</td>
                                            <td className="px-5 py-4 capitalize text-black/70">{user.gender || "—"}</td>
                                            <td className="px-5 py-4 capitalize text-black/70">{user.attended || "—"}</td>
                                            <td className="px-5 py-4 capitalize text-black/70">{user.member || "—"}</td>
                                            <td className="px-5 py-4 text-black/70">
                                                {user.createdAt ? new Date(user.createdAt.replace(" ", "T")).toLocaleDateString() : String(user.registrationYear)}
                                            </td>
                                            <td className="px-5 py-4">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => setSelectedUser(user)}
                                                        className="rounded-md border border-black/15 p-2 text-black/60 hover:bg-black/5 hover:text-black"
                                                        title="View registration"
                                                        aria-label={`View ${user.firstName} ${user.lastName}`}
                                                    >
                                                        <Eye size={17} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(user)}
                                                        disabled={deletingId === user.id}
                                                        className="rounded-md border border-red-200 p-2 text-red-600 hover:bg-red-50 disabled:opacity-40"
                                                        title="Delete registration"
                                                        aria-label={`Delete ${user.firstName} ${user.lastName}`}
                                                    >
                                                        <Trash2 size={17} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <p className="border-t border-black/10 px-5 py-3 font-inter text-xs text-black/50">
                        Showing {filteredUsers.length} of {users.length} registrations
                    </p>
                </section>
            </div>

            {selectedUser && (
                <div
                    className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-6"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="registration-details-title"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) setSelectedUser(null);
                    }}
                >
                    <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl bg-white p-6 shadow-2xl sm:rounded-2xl sm:p-8">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="font-inter text-sm text-black/50">Registration #{selectedUser.id}</p>
                                <h2 id="registration-details-title" className="mt-1 font-clashSemibold text-2xl">
                                    {selectedUser.firstName} {selectedUser.lastName}
                                </h2>
                            </div>
                            <button
                                onClick={() => setSelectedUser(null)}
                                className="rounded-md p-2 text-black/50 hover:bg-black/5 hover:text-black"
                                aria-label="Close details"
                            >
                                <X size={21} />
                            </button>
                        </div>
                        <dl className="mt-7 grid gap-x-8 gap-y-5 sm:grid-cols-2">
                            {[
                                ["Email", selectedUser.email],
                                ["Phone", selectedUser.phone],
                                ["Gender", selectedUser.gender],
                                ["Attended previous edition", selectedUser.attended],
                                ["NewBrook member", selectedUser.member],
                                ["Invited by / heard through", selectedUser.invitedBy],
                                ["Affiliate", selectedUser.affiliate],
                                ["Local church", selectedUser.localChurch],
                                ["Registration year", String(selectedUser.registrationYear)],
                                ["Created at", selectedUser.createdAt || "Not recorded"],
                            ].map(([label, value]) => (
                                <div key={label}>
                                    <dt className="font-inter text-xs font-semibold uppercase tracking-wider text-black/45">
                                        {label}
                                    </dt>
                                    <dd className="mt-1 break-words font-inter text-sm capitalize text-black/80">
                                        {value || "—"}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                        <button
                            onClick={() => handleDelete(selectedUser)}
                            disabled={deletingId === selectedUser.id}
                            className="mt-8 inline-flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2.5 font-inter text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40"
                        >
                            <Trash2 size={17} />
                            {deletingId === selectedUser.id ? "Deleting…" : "Delete registration"}
                        </button>
                    </div>
                </div>
            )}
        </main>
    );
};

export default Admin;
